import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "react-oidc-context";
import { supportTicketService } from "@/services";
import type { SupportTicketDto } from "@shared/types/SupportTicketDto";
import { SupportTicketPayload } from "@/domain/SupportTicketPayload";

interface SupportTicketsContextType {
  tickets: SupportTicketDto[];
  currentTicket: SupportTicketDto | null;
  isLoading: boolean;
  error: string | null;
  fetchTickets: () => Promise<SupportTicketDto[]>;
  fetchTicket: (ticketId: string) => Promise<void>;
  createTicket: (data: SupportTicketPayload) => Promise<SupportTicketDto>;
  addMessage: (ticketId: string, message: string) => Promise<void>;
  resolveTicket: (ticketId: string, reason: string) => Promise<void>;
  refreshTickets: () => Promise<void>;
  refreshCurrentTicket: () => Promise<void>;
}

const SupportTicketsContext = createContext<SupportTicketsContextType | null>(
  null
);

export function SupportTicketsProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [tickets, setTickets] = useState<SupportTicketDto[]>([]);
  const [currentTicket, setCurrentTicket] = useState<SupportTicketDto | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async (): Promise<SupportTicketDto[]> => {
    if (!auth.isAuthenticated) return [];

    try {
      return await supportTicketService.getTickets();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch tickets");
      console.error("Failed to fetch tickets:", err);
    }
    return [];
  }, [auth.isAuthenticated]);

  const fetchTicket = useCallback(
    async (ticketId: string) => {
      if (!auth.isAuthenticated) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await supportTicketService.getTicket(ticketId);
        setCurrentTicket(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch ticket");
        console.error("Failed to fetch ticket:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [auth.isAuthenticated]
  );

  const createTicket = useCallback(
    async (data: SupportTicketPayload): Promise<SupportTicketDto> => {
      if (!auth.isAuthenticated) {
        throw new Error("Not authenticated");
      }

      const newTicket = await supportTicketService.createTicket(data);
      setTickets((prev) => [newTicket, ...prev]);
      return newTicket;
    },
    [auth.isAuthenticated]
  );

  const addMessage = useCallback(
    async (ticketId: string, message: string) => {
      if (!auth.isAuthenticated) {
        throw new Error("Not authenticated");
      }

      const result = await supportTicketService.addMessage({
        ticketId,
        message: {
          message,
          createdAt: new Date().toISOString(),
          senderName: auth.user?.profile.name as string,
          attachments: [],
        },
      });

      setCurrentTicket((prev) => {
        if (!prev || prev.ticketId !== ticketId) return prev;
        return {
          ...prev,
          messages: [...prev.messages, result.message],
        };
      });

      setTickets((prev) =>
        prev.map((ticket) => {
          if (ticket.ticketId !== ticketId) return ticket;
          return {
            ...ticket,
            messages: [...ticket.messages, result.message],
          };
        })
      );
    },
    [auth.isAuthenticated]
  );

  const resolveTicket = useCallback(
    async (ticketId: string, reason: string) => {
      if (!auth.isAuthenticated) {
        throw new Error("Not authenticated");
      }

      await supportTicketService.resolveTicket({ ticketId, reason });

      const resolvedAt = new Date().toISOString();

      setCurrentTicket((prev) => {
        if (!prev || prev.ticketId !== ticketId) return prev;
        return {
          ...prev,
          isResolved: true,
          resolvedAt,
          resolvedReason: reason,
        };
      });

      setTickets((prev) =>
        prev.map((ticket) => {
          if (ticket.ticketId !== ticketId) return ticket;
          return {
            ...ticket,
            isResolved: true,
            resolvedAt,
            resolvedReason: reason,
          };
        })
      );
    },
    [auth.isAuthenticated]
  );

  const refreshTickets = useCallback(async () => {
    await fetchTickets();
  }, [fetchTickets]);

  const refreshCurrentTicket = useCallback(async () => {
    if (currentTicket) {
      await fetchTicket(currentTicket.ticketId);
    }
  }, [currentTicket, fetchTicket]);

  return (
    <SupportTicketsContext.Provider
      value={{
        tickets,
        currentTicket,
        isLoading,
        error,
        fetchTickets,
        fetchTicket,
        createTicket,
        addMessage,
        resolveTicket,
        refreshTickets,
        refreshCurrentTicket,
      }}
    >
      {children}
    </SupportTicketsContext.Provider>
  );
}

export function useSupportTickets() {
  const context = useContext(SupportTicketsContext);
  if (!context) {
    throw new Error(
      "useSupportTickets must be used within a SupportTicketsProvider"
    );
  }
  return context;
}
